import type { CoupleSpace } from '../../domain/types';
import type { LocalStats } from '../../domain/stats';
import { MAX_BACKUP_FILE_BYTES, type BackupPreview } from '../../storage/backup';
import type { BackupConflict } from '../../storage/repository';

export type SettingsPanel =
  | { kind: 'idle' }
  | { kind: 'export' }
  | { kind: 'invite'; inviteUrl: string; fresh: boolean }
  | { kind: 'rebuild' }
  | { kind: 'clear' }
  | { kind: 'import-preview'; preview: BackupPreview }
  | { kind: 'backup-conflicts'; conflicts: BackupConflict[] };

export interface SettingsModel {
  space: CoupleSpace | null;
  stats: LocalStats | null;
  panel: SettingsPanel;
  notice?: string;
  error?: string;
}

export interface SettingsActions {
  onOpenExport?: () => void;
  onExport?: (passphrase: string, confirmation: string) => void | Promise<void>;
  onInspectImport?: (file: File, passphrase: string) => void | Promise<void>;
  onRejectOversizedImport?: () => void;
  onConfirmImport?: () => void | Promise<void>;
  onRegenerateInvite?: () => void | Promise<void>;
  onOpenRebuild?: () => void;
  onRebuild?: (spaceName: string) => void | Promise<void>;
  onOpenClear?: () => void;
  onClear?: (spaceName: string) => void | Promise<void>;
  onCopy?: (value: string, status: HTMLElement) => void | Promise<void>;
  onResolveBackupConflict?: (id: string, keep: 'local' | 'incoming') => void | Promise<void>;
}

export function renderSettingsView(
  container: HTMLElement,
  model: SettingsModel,
  actions: SettingsActions = {},
): void {
  container.replaceChildren();
  const section = document.createElement('section');
  section.className = 'settings-view stack';

  const header = document.createElement('div');
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow';
  eyebrow.textContent = '只保存在这台设备';
  const title = document.createElement('h1');
  title.textContent = '我们';
  header.append(eyebrow, title);
  section.append(header);

  if (model.space && model.stats) {
    section.append(renderSpaceSummary(model.space, model.stats));
    const actionsCard = document.createElement('div');
    actionsCard.className = 'page-card stack';
    const actionsTitle = document.createElement('h2');
    actionsTitle.textContent = '双人空间与数据';
    actionsCard.append(
      actionsTitle,
      actionButton('重新生成邀请', () => actions.onRegenerateInvite?.()),
      actionButton('导出加密备份', () => actions.onOpenExport?.()),
      actionButton('建立新的双人空间', () => actions.onOpenRebuild?.()),
      actionButton('清除本机数据', () => actions.onOpenClear?.(), 'button button--danger'),
    );
    section.append(actionsCard);
  } else {
    const empty = document.createElement('p');
    empty.className = 'page-card muted';
    empty.textContent = '这台设备还没有双人空间，可以从下方导入加密备份。';
    section.append(empty);
  }

  section.append(renderImportForm(actions));
  renderPanel(section, model, actions);
  appendMessage(section, model.notice, 'status');
  appendMessage(section, model.error, 'alert');
  container.append(section);
}

function renderSpaceSummary(space: CoupleSpace, stats: LocalStats): HTMLElement {
  const card = document.createElement('div');
  card.className = 'page-card stack';
  const heading = document.createElement('h2');
  heading.textContent = space.spaceName;
  const identity = document.createElement('p');
  identity.textContent = `${space.selfAvatar} ${space.selfName} 和 ${space.partnerAvatar} ${space.partnerName}`;
  const anniversary = document.createElement('p');
  anniversary.className = 'settings-anniversary';
  anniversary.textContent = `一起记录第 ${stats.anniversaryDays ?? '—'} 天`;
  const localOnly = document.createElement('div');
  localOnly.className = 'settings-stats';
  localOnly.setAttribute('aria-label', '以本机已收下的记录为准');
  for (const value of [
    `奖励 ${stats.rewards}`,
    `小罚单 ${stats.penalties}`,
    `已完成 ${stats.completed}`,
  ]) {
    const item = document.createElement('span');
    item.textContent = value;
    localOnly.append(item);
  }
  const scope = document.createElement('p');
  scope.className = 'muted';
  scope.textContent = '以上统计以本机已收下的记录为准，不代表云端或对方设备的全部记录。';
  card.append(heading, identity, anniversary, localOnly, scope);
  if (stats.topTags.length > 0) {
    const tags = document.createElement('p');
    tags.className = 'muted';
    tags.textContent = `常用标签：${stats.topTags.map(({ label, count }) => `${label}×${count}`).join('、')}`;
    card.append(tags);
  }
  return card;
}

function renderImportForm(actions: SettingsActions): HTMLElement {
  const card = document.createElement('div');
  card.className = 'page-card stack';
  const title = document.createElement('h2');
  title.textContent = '导入恢复';
  const explanation = document.createElement('p');
  explanation.className = 'muted';
  explanation.textContent = '先验证口令并预览内容，确认前不会写入本机数据。';
  const form = document.createElement('form');
  form.className = 'stack';
  form.innerHTML = `
    <label class="field">导入备份<input name="backup" type="file" accept="application/json,.json" required></label>
    <label class="field">导入口令<input name="passphrase" type="password" autocomplete="current-password" required></label>
    <button class="button" type="submit">预览备份</button>
  `;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const fileInput = form.elements.namedItem('backup');
    const passphrase = form.elements.namedItem('passphrase');
    if (!(fileInput instanceof HTMLInputElement) || !(passphrase instanceof HTMLInputElement)) return;
    const file = fileInput.files?.[0];
    if (!file) return;
    if (file.size > MAX_BACKUP_FILE_BYTES) {
      actions.onRejectOversizedImport?.();
      return;
    }
    void actions.onInspectImport?.(file, passphrase.value);
  });
  card.append(title, explanation, form);
  return card;
}

function renderPanel(section: HTMLElement, model: SettingsModel, actions: SettingsActions): void {
  if (model.panel.kind === 'idle') return;
  const card = document.createElement('div');
  card.className = 'page-card stack settings-panel';

  if (model.panel.kind === 'export') renderExport(card, actions);
  if (model.panel.kind === 'invite') renderInvite(card, model.panel, actions);
  if (model.panel.kind === 'rebuild' && model.space) {
    renderConfirmation(card, {
      title: '建立新的双人空间',
      warning: '旧链接将无法在新空间中打开；旧空间的本机记录会被清除。',
      spaceName: model.space.spaceName,
      buttonText: '我已备份，继续建立',
      onConfirm: actions.onRebuild,
    });
  }
  if (model.panel.kind === 'clear' && model.space) {
    const backup = actionButton('先导出备份', () => actions.onOpenExport?.());
    card.append(backup);
    renderConfirmation(card, {
      title: '清除本机数据',
      warning: '此操作只清除这台设备，无法撤回，也不会清除 TA 设备上的记录。',
      spaceName: model.space.spaceName,
      buttonText: '确认清除本机数据',
      onConfirm: actions.onClear,
      danger: true,
    });
  }
  if (model.panel.kind === 'import-preview') renderImportPreview(card, model.panel.preview, actions);
  if (model.panel.kind === 'backup-conflicts') renderBackupConflicts(card, model.panel.conflicts, actions);
  section.append(card);
}

function renderBackupConflicts(card: HTMLElement, conflicts: BackupConflict[], actions: SettingsActions): void {
  const title = document.createElement('h2');
  title.textContent = '备份中的分叉需要选择';
  card.append(title);
  for (const conflict of conflicts) {
    const item = document.createElement('section');
    item.className = 'stack warning';
    item.innerHTML = `<p>《${escapeText(conflict.local.title)}》有两条合法分支；未选择前两份数据都已保留。</p>`;
    item.append(
      actionButton('保留本机版本', () => actions.onResolveBackupConflict?.(conflict.id, 'local')),
      actionButton('保留备份版本并生成传播链接', () => actions.onResolveBackupConflict?.(conflict.id, 'incoming'), 'button button--primary'),
    );
    card.append(item);
  }
}

function escapeText(value: string): string {
  const node = document.createElement('span');
  node.textContent = value;
  return node.innerHTML;
}

function renderExport(card: HTMLElement, actions: SettingsActions): void {
  const title = document.createElement('h2');
  title.textContent = '导出加密备份';
  const explanation = document.createElement('p');
  explanation.className = 'muted';
  explanation.textContent = '备份包含双人空间密钥。请设置口令并妥善保存，口令无法找回。';
  const form = document.createElement('form');
  form.className = 'stack';
  form.innerHTML = `
    <label class="field">备份口令<input name="passphrase" type="password" autocomplete="new-password" required></label>
    <label class="field">再次输入备份口令<input name="confirmation" type="password" autocomplete="new-password" required></label>
    <button class="button button--primary" type="submit" disabled>下载加密备份</button>
  `;
  const passphrase = form.elements.namedItem('passphrase');
  const confirmation = form.elements.namedItem('confirmation');
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  const update = (): void => {
    if (!(passphrase instanceof HTMLInputElement) || !(confirmation instanceof HTMLInputElement) || !submit) return;
    submit.disabled = passphrase.value.trim().length === 0 || passphrase.value !== confirmation.value;
  };
  if (passphrase instanceof HTMLInputElement) passphrase.addEventListener('input', update);
  if (confirmation instanceof HTMLInputElement) confirmation.addEventListener('input', update);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (passphrase instanceof HTMLInputElement && confirmation instanceof HTMLInputElement) {
      void actions.onExport?.(passphrase.value, confirmation.value);
    }
  });
  card.append(title, explanation, form);
}

function renderInvite(
  card: HTMLElement,
  panel: Extract<SettingsPanel, { kind: 'invite' }>,
  actions: SettingsActions,
): void {
  const title = document.createElement('h2');
  title.textContent = panel.fresh ? '新的双人空间已建立' : '邀请已重新生成';
  const note = document.createElement('p');
  note.className = 'muted';
  note.textContent = panel.fresh
    ? '新空间使用新的身份与密钥。下一步只把新邀请发给你的伴侣。'
    : '空间身份与密钥未改变。请只把邀请发给你的伴侣。';
  const label = document.createElement('label');
  label.className = 'field';
  label.append(document.createTextNode('邀请链接'));
  const input = document.createElement('input');
  input.readOnly = true;
  input.value = panel.inviteUrl;
  input.dataset.testid = 'share-link';
  label.append(input);
  const status = document.createElement('p');
  status.className = 'muted';
  status.setAttribute('role', 'status');
  card.append(title, note, label, actionButton('复制邀请链接', () => actions.onCopy?.(panel.inviteUrl, status)), status);
}

interface ConfirmationOptions {
  title: string;
  warning: string;
  spaceName: string;
  buttonText: string;
  onConfirm?: (spaceName: string) => void | Promise<void>;
  danger?: boolean;
}

function renderConfirmation(card: HTMLElement, options: ConfirmationOptions): void {
  const title = document.createElement('h2');
  title.textContent = options.title;
  const warning = document.createElement('p');
  warning.className = 'warning';
  warning.textContent = options.warning;
  const form = document.createElement('form');
  form.className = 'stack';
  form.innerHTML = `
    <label class="field">输入双人空间名称以确认<input name="spaceName" autocomplete="off" required></label>
    <button class="${options.danger ? 'button button--danger' : 'button button--primary'}" type="submit" disabled></button>
  `;
  const input = form.elements.namedItem('spaceName');
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (submit) submit.textContent = options.buttonText;
  if (input instanceof HTMLInputElement) input.addEventListener('input', () => {
    if (input instanceof HTMLInputElement && submit) submit.disabled = input.value !== options.spaceName;
  });
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (input instanceof HTMLInputElement && input.value === options.spaceName) {
      void options.onConfirm?.(input.value);
    }
  });
  card.append(title, warning, form);
}

function renderImportPreview(card: HTMLElement, preview: BackupPreview, actions: SettingsActions): void {
  const title = document.createElement('h2');
  title.textContent = '备份预览';
  const facts = document.createElement('dl');
  facts.className = 'ticket-facts';
  appendFact(facts, '空间名称', preview.spaceName);
  appendFact(facts, '单据数量', String(preview.ticketCount));
  appendFact(facts, '备份日期', new Date(preview.createdAt).toLocaleString('zh-CN'));
  const note = document.createElement('p');
  note.className = 'muted';
  note.textContent = '确认后会按事件 UUID 原子合并，不会整库覆盖。';
  card.append(title, facts, note, actionButton('确认恢复备份', () => actions.onConfirmImport?.(), 'button button--primary'));
}

function appendFact(list: HTMLDListElement, label: string, value: string): void {
  const row = document.createElement('div');
  const term = document.createElement('dt');
  term.textContent = label;
  const detail = document.createElement('dd');
  detail.textContent = `${label}：${value}`;
  row.append(term, detail);
  list.append(row);
}

function actionButton(text: string, action: () => void | Promise<void> | undefined, className = 'button'): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = className;
  button.textContent = text;
  button.addEventListener('click', () => void action());
  return button;
}

function appendMessage(container: HTMLElement, message: string | undefined, role: 'status' | 'alert'): void {
  if (!message) return;
  const element = document.createElement('p');
  if (role === 'alert') {
    element.id = 'settings-error';
    container.setAttribute('aria-describedby', element.id);
  }
  element.className = role === 'alert' ? 'error' : 'success-notice';
  element.setAttribute('role', role);
  element.textContent = message;
  container.append(element);
}
